// Pure utility functions for HexWorldMap — no React dependencies.
// Generic constraints used instead of local interfaces to avoid name conflicts.

// Mirrors the backend's HexNeighbors.GetOddROffsetNeighbors (Services/HexNeighbors.cs).
// Tiles are stored/rendered in odd-r offset coordinates (odd rows shift right by half
// a hex - see getHexPosition in HexGrid.tsx), so the four diagonal neighbors depend on
// row parity; a single fixed axial delta set is only correct for one parity (GH-78).
export function getOddROffsetNeighbors(x: number, y: number): { x: number; y: number }[] {
  const isOddRow = y % 2 !== 0;
  const deltas = isOddRow
    ? [[1, 0], [-1, 0], [1, -1], [0, -1], [1, 1], [0, 1]]
    : [[1, 0], [-1, 0], [0, -1], [-1, -1], [0, 1], [-1, 1]];
  return deltas.map(([dx, dy]) => ({ x: x + dx, y: y + dy }));
}

// Mirrors the backend's HexNeighbors.OddRToAxial (Services/HexNeighbors.cs).
export function oddRToAxial(x: number, y: number): { q: number; r: number } {
  const q = x - (y - (y & 1)) / 2;
  return { q, r: y };
}

// Tiles are stored/rendered in odd-r offset coordinates, not true axial - a plain cube
// distance on (x,y) is wrong for most pairs that aren't in the same row. Convert to axial
// first (GH-81, same root cause as the GH-78 neighbor-math bug).
export function calculateHexDistance(
  tile1: { x: number; y: number } | null,
  tile2: { x: number; y: number }
): number {
  if (!tile1) return 0;
  const { q: q1, r: r1 } = oddRToAxial(tile1.x, tile1.y);
  const { q: q2, r: r2 } = oddRToAxial(tile2.x, tile2.y);
  const s1 = -q1 - r1, s2 = -q2 - r2;
  return (Math.abs(q1 - q2) + Math.abs(r1 - r2) + Math.abs(s1 - s2)) / 2;
}

export function getTownAtTile<T extends { worldX: number; worldY: number }>(
  towns: T[], x: number, y: number
): T | undefined {
  return towns.find(t => t.worldX === x && t.worldY === y);
}

export function getTownIcon(townType: string): string {
  switch (townType) {
    case 'Mining':   return '⛏️';
    case 'Forest':   return '🌲';
    case 'Trading':  return '🏪';
    case 'Military': return '⚔️';
    case 'Magical':  return '🔮';
    default:         return '🏰';
  }
}

export function getRarityColor(rarity: number): string {
  const colors = ['#9e9e9e', '#4caf50', '#2196f3', '#9c27b0', '#ff9800'];
  return colors[rarity - 1] || '#666';
}

export function getNearestTownHint(
  tileX: number,
  tileY: number,
  towns: Array<{ worldX: number; worldY: number }>
): { bearingDeg: number; opacity: number } | null {
  const MAX_DISTANCE = 6;
  let nearest: { worldX: number; worldY: number } | null = null;
  let nearestDist = Infinity;

  for (const town of towns) {
    const dist = calculateHexDistance({ x: tileX, y: tileY }, { x: town.worldX, y: town.worldY });
    if (dist > 0 && dist <= MAX_DISTANCE && dist < nearestDist) {
      nearest = town;
      nearestDist = dist;
    }
  }

  if (!nearest) return null;

  // Compute direction in screen space using the same odd-r offset position formula the
  // map actually renders with (HexGrid.tsx's getHexPosition) - treating (x,y) deltas as
  // axial q/r deltas here was the same offset-vs-axial bug as GH-78/GH-81 (size cancels
  // out of atan2, so it's dropped).
  const p1 = { cx: Math.sqrt(3) * (tileX + 0.5 * (tileY & 1)), cy: 1.5 * tileY };
  const p2 = { cx: Math.sqrt(3) * (nearest.worldX + 0.5 * (nearest.worldY & 1)), cy: 1.5 * nearest.worldY };
  const scrDX = p2.cx - p1.cx;
  const scrDY = p2.cy - p1.cy;
  const rawBearing = Math.atan2(scrDY, scrDX);

  // Tier by distance: far = faint + coarse direction; near = sharp + true bearing
  let opacity: number;
  let snapStep: number;
  if (nearestDist >= 5) {
    opacity = 0.35;
    snapStep = Math.PI / 2; // 4 cardinals
  } else if (nearestDist >= 3) {
    opacity = 0.55;
    snapStep = Math.PI / 4; // 8 directions
  } else {
    opacity = 0.75;
    snapStep = 0;            // true bearing
  }

  const bearingDeg = snapStep > 0
    ? Math.round(rawBearing / snapStep) * snapStep * (180 / Math.PI)
    : rawBearing * (180 / Math.PI);

  return { bearingDeg, opacity };
}

export function getVisibleTiles<T extends { id: number; x: number; y: number }>(
  tiles: T[], currentTileId: number, viewRadius: number
): T[] {
  if (tiles.length === 0) return [];
  const currentTile = tiles.find(t => t.id === currentTileId);
  if (!currentTile) return tiles;
  return tiles.filter(tile => {
    const dx = tile.x - currentTile.x;
    const dy = tile.y - currentTile.y;
    return Math.sqrt(dx * dx + dy * dy) <= viewRadius;
  });
}
